/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, { useRef, useState } from 'react';
import classNames from 'classnames/bind';
import Lightbox from 'yet-another-react-lightbox';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';

import PhotoGrid from 'components/PhotoGrid/PhotoGrid';

import { TFunction } from 'next-i18next';
import VideoPlayer from 'components/VideoPlayer/VideoPlayer';

import { PostAttachmentType } from '__generated__/graphql/legacy/graphql';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import { getFileExtension } from 'utils/fileHelper';
import { getFileIcon } from 'components/AttachmentPreview/FilePreview';
import { Button } from '@thinkific/chalk';
import { DownloadSimple } from '@thinkific/chalk/react-icons';
import { BREAKPOINT_MD } from 'utils/constants';
import { useWindowSize } from 'hooks/useWindowSize';
import { Attachment } from 'schema/Post/types';
import styles from './AttachmentDisplayer.module.scss';

const cx = classNames.bind(styles);

interface AttachmentDisplayerProps {
  attachments: Attachment[];
  isCommentAttachment?: boolean;
  isReplyPanel?: boolean;
  t: TFunction;
  isComment?: boolean;
}

const renderFileIcon = (fileName: string) => {
  const fileType = getFileExtension(fileName);
  const fileIcon = getFileIcon(fileType);
  return fileIcon;
};

const AttachmentDisplayer = ({
  attachments,
  t,
  isCommentAttachment,
  isReplyPanel,
  isComment,
}: AttachmentDisplayerProps) => {
  const { width } = useWindowSize();
  const isMobile = width < BREAKPOINT_MD;

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const thumbnailsRef = useRef(null);

  const filterAttachments = (type: PostAttachmentType) =>
    attachments.filter(
      (attachment) => attachment.attachmentType === type && attachment.url
    );

  const imageAttachments = filterAttachments(PostAttachmentType.Image);
  const videoAttachments = filterAttachments(PostAttachmentType.Video);
  const fileAttachments = filterAttachments(PostAttachmentType.File);

  return (
    <div
      className={cx('attachment-displayer', {
        'attachment-displayer--comment': isCommentAttachment,
      })}
    >
      {videoAttachments.map((attachment) => (
        <div
          className={cx('video__container')}
          data-qa="video-container"
          key={attachment.url}
        >
          <VideoPlayer videoUrl={attachment?.url || ''} />
        </div>
      ))}
      <PhotoGrid
        images={(imageAttachments as Attachment[]) || []}
        isComment={isComment}
        isReplyPanel={isReplyPanel}
        onClick={(event, index) => {
          event.preventDefault();
          event.stopPropagation();
          setLightboxOpen(true);
          setLightboxIndex(index);
        }}
      />

      {fileAttachments.map((attachment) => (
        <div
          className={cx('file__container')}
          data-qa="file-container"
          key={attachment.url}
        >
          <div className={cx('file__details')}>
            {renderFileIcon(attachment.fileName || '')}
            <span className={cx('file__details__name')}>
              {attachment.fileName}
            </span>
          </div>
          <div className={cx('file__actions')}>
            <Button
              data-qa="file-container__download-button"
              emphasis="low"
              hiddenLabel={isMobile}
              icon={DownloadSimple}
              iconPosition="lead"
              size="sm"
              variant="default"
              onClick={() => {
                window.open(attachment.url || '', '_blank');
              }}
            >
              {t('Download')}
            </Button>
          </div>
        </div>
      ))}
      {/* To stop lightbox propagating click events to parent elements */}
      <div
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <Lightbox
          close={() => setLightboxOpen(false)}
          controller={{
            closeOnBackdropClick: true,
          }}
          data-qa="lightbox"
          index={lightboxIndex}
          open={lightboxOpen}
          plugins={[Thumbnails]}
          slides={imageAttachments.map((attachment) => ({
            src: attachment.url || '',
          }))}
          thumbnails={{ ref: thumbnailsRef }}
        />
      </div>
    </div>
  );
};

export default AttachmentDisplayer;
